/**
 * Facebook Messenger Platform Message Reactions Webhook Types
 * 
 * These types define the structure of webhook events received when users
 * react to messages in Messenger conversations.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/message-reactions
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
} from './base-types';

/**
 * Types of reactions that can be applied to messages in Messenger.
 * These are the predefined reaction types supported by Facebook.
 */
export enum MessageReactionType {
  /** Standard like reaction */
  LIKE = 'like',
  /** Dislike reaction */
  DISLIKE = 'dislike',
  /** Love reaction (heart) */
  LOVE = 'love',
  /** Sad reaction */
  SAD = 'sad',
  /** Angry reaction */
  ANGRY = 'angry',
  /** Wow/surprised reaction */
  WOW = 'wow',
  /** Smile/laugh reaction */
  SMILE = 'smile',
  /** Other/unrecognized emoji reactions */
  OTHER = 'other'
}

/**
 * Actions that can be performed on message reactions.
 */
export enum MessageReactionAction {
  /** Adding a reaction to a message */
  REACT = 'react',
  /** Removing a reaction from a message */
  UNREACT = 'unreact'
}

// Note: MessageReactionSender and MessageReactionRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

import { WebhookSender as MessageReactionSender } from './base-types';
import { WebhookRecipient as MessageReactionRecipient } from './base-types';
export { MessageReactionSender, MessageReactionRecipient };

/**
 * Contains the detailed information about the reaction.
 */
export interface MessageReactionData {
  /** 
   * The type of reaction applied to the message.
   * Can be one of the predefined types or "other" for unrecognized emojis.
   */
  reaction: MessageReactionType;
  
  /** 
   * The UTF-8 emoji representation of the reaction (optional).
   * Example: "\u{2764}\u{FE0F}" for heart emoji
   */
  emoji?: string;
  
  /** 
   * The action performed - either adding or removing the reaction.
   */
  action: MessageReactionAction;
  
  /** 
   * The message ID that the reaction was applied to.
   * This corresponds to the 'mid' field of the original message.
   */
  mid: string;
}

/**
 * Complete webhook event structure for message reactions with discriminator.
 * This is the main payload received when a user reacts to a message.
 */
export interface MessageReactionWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGE_REACTION;
  
  /** Detailed information about the reaction */
  reaction: MessageReactionData;
}

/**
 * The complete webhook payload containing the message reaction event.
 * This matches the structure of the webhook POST request body.
 */
export interface MessageReactionWebhookPayload extends WebhookPayload<MessageReactionWebhookEvent> {}

/**
 * Context object for processing message reaction webhooks.
 * Useful for handlers that need additional processing information.
 */
export interface MessageReactionProcessingContext {
  /** The original webhook event */
  event: MessageReactionWebhookEvent;
  
  /** Page ID that received the reaction */
  pageId: string;
  
  /** User ID who performed the reaction */
  userId: string;
  
  /** ID of the message that was reacted to */
  messageId: string;
  
  /** Whether this is a new reaction (true) or removal (false) */
  isReactionAdded: boolean;
  
  /** The type of reaction */
  reactionType: MessageReactionType;
  
  /** Raw emoji string if available */
  emoji?: string;
  
  /** Timestamp when the reaction occurred */
  timestamp: Date;
}

/**
 * Helper type for reaction statistics and aggregation.
 * Useful for tracking reaction counts on messages.
 */
export interface MessageReactionStats {
  /** The message ID these stats apply to */
  messageId: string;
  
  /** Count of each reaction type */
  reactions: {
    [K in MessageReactionType]?: number;
  };
  
  /** Total number of reactions */
  totalReactions: number;
  
  /** Last updated timestamp */
  lastUpdated: Date;
}

/**
 * Configuration options for handling message reaction webhooks.
 */
export interface MessageReactionWebhookConfig {
  /** Whether to track reaction statistics */
  enableStats?: boolean;
  
  /** Whether to handle emoji reactions beyond predefined types */
  handleCustomEmojis?: boolean;
  
  /** Maximum age of reactions to process (in milliseconds) */
  maxReactionAge?: number;
  
  /** Whether to validate webhook signatures */
  validateSignature?: boolean;
}

/* Example webhook payload structure:
{
  "object": "page",
  "entry": [
    {
      "id": "PAGE_ID",
      "time": 1458668856463,
      "messaging": [
        {
          "sender": {
            "id": "USER_PSID"
          },
          "recipient": {
            "id": "PAGE_ID"
          },
          "timestamp": 1458668856463,
          "reaction": {
            "reaction": "love",
            "emoji": "\u{2764}\u{FE0F}",
            "action": "react",
            "mid": "MESSAGE_ID"
          }
        }
      ]
    }
  ]
}
*/

/* Example of removing a reaction:
{
  "object": "page",
  "entry": [
    {
      "id": "PAGE_ID", 
      "time": 1458668856463,
      "messaging": [
        {
          "sender": {
            "id": "USER_PSID"
          },
          "recipient": {
            "id": "PAGE_ID"
          },
          "timestamp": 1458668856463,
          "reaction": {
            "reaction": "love",
            "action": "unreact",
            "mid": "MESSAGE_ID"
          }
        }
      ]
    }
  ]
}
*/